import React, { RefObject } from 'react';

import styled from 'styled-components';

import ArrowIcon2 from '~/icons/components/ArrowIcon2';
import PauseIcon2 from '~/icons/components/PauseIcon2';
import PlayIcon2 from '~/icons/components/PlayIcon2';
import { mobile } from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';

import {
  haloRingBase,
  haloRingHover,
  haloRingPressed,
} from '../../styles/haloRing';

import { HORIZONTAL_PADDING, HORIZONTAL_PADDING_MOBILE } from './constants';

const DiscoveryGuideControlsRow = styled.div<{ $hasOverflow: boolean }>`
  padding: 16px ${HORIZONTAL_PADDING}px;
  display: flex;
  align-items: center;
  justify-content: ${({ $hasOverflow }) =>
    $hasOverflow ? 'space-between' : 'flex-start'};
  gap: 16px;

  ${({ theme }) => mobile`
    padding: 24px ${HORIZONTAL_PADDING_MOBILE}px ${HORIZONTAL_PADDING_MOBILE}px;
    justify-content: flex-start;
    border-top: 1px solid ${theme.colors.gridV2FilterSeparator};
  `}
`;

const PlayPauseButton = styled.button`
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid transparent;
  background: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  ${haloRingBase};

  &::before {
    inset: -1px;
    border-radius: inherit;
  }

  &:hover::before {
    ${haloRingHover};
  }

  &:active::before {
    ${haloRingPressed};
  }
`;

const ScrollbarTrack = styled.div`
  width: 200px;
  height: 2px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gridV2DgScrollbarTrack};
  position: relative;

  ${mobile`
    width: 120px;
  `}
`;

const ScrollbarThumb = styled.div<{
  $widthPercent: number;
  $isReady: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $widthPercent }) => `${$widthPercent}%`};
  height: 2px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gridV2DgScrollbarThumb};
  pointer-events: none;
  will-change: left;
  opacity: ${({ $isReady }) => ($isReady ? 1 : 0)};
  transition: opacity 180ms ease;
`;

const ScrollNavControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  ${mobile`
    display: none;
  `}
`;

const ScrollNavButton = styled.button`
  position: relative;
  width: 20px;
  height: 20px;
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.gridV2NumResultsText};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  ${haloRingBase};

  &::before {
    inset: -2px;
    border-radius: inherit;
  }

  &:hover::before {
    ${haloRingHover};
  }

  &:active::before {
    ${haloRingPressed};
  }
`;

const ScrollNavControlSlot = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const StyledPauseIcon2 = styled(PauseIcon2).attrs(({ theme }) => ({
  color: theme.colors.gridV2DgPlayPauseIcon,
}))``;

const StyledPlayIcon2 = styled(PlayIcon2).attrs(({ theme }) => ({
  color: theme.colors.gridV2DgPlayPauseIcon,
}))``;

const ScrollNavArrowIcon = styled(ArrowIcon2)`
  display: block;
`;

const ScrollNavArrowIconLeft = styled(ScrollNavArrowIcon)`
  transform: rotate(180deg);
`;

const ScrollNavDivider = styled.span`
  width: 24px;
  height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;

  &::before {
    content: '';
    width: 1px;
    height: 12px;
    border-radius: 64px;
    background: ${({ theme }) => theme.colors.gridV2DgScrollNavDivider};
  }
`;

const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
`;

const LabelText = styled.span`
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: ${16 / 14};
  transform: translateY(-1px);
`;

type DiscoveryGuideControlsProps = {
  hasOverflow: boolean;
  hasVideoAssets: boolean;
  isPlayingAllVideos: boolean;
  label?: string;
  onTogglePlayPause: () => void;
  scrollbarThumbRef: RefObject<HTMLDivElement | null>;
  thumbWidthPercent: number;
  isScrollbarThumbReady: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  onScrollLeft: () => void;
  onScrollRight: () => void;
};

const DiscoveryGuideControls = ({
  hasOverflow,
  hasVideoAssets,
  isPlayingAllVideos,
  label,
  onTogglePlayPause,
  scrollbarThumbRef,
  thumbWidthPercent,
  isScrollbarThumbReady,
  canScrollLeft,
  canScrollRight,
  onScrollLeft,
  onScrollRight,
}: DiscoveryGuideControlsProps) => {
  const { formatMessage } = useIntl();

  const showControlsRow = hasOverflow || hasVideoAssets || !!label;

  if (!showControlsRow) {
    return null;
  }

  return (
    <DiscoveryGuideControlsRow $hasOverflow={hasOverflow}>
      <ControlsLeft>
        {label && <LabelText>{label}</LabelText>}
        {hasVideoAssets && (
          <PlayPauseButton
            type="button"
            aria-label={
              isPlayingAllVideos
                ? formatMessage({
                    id: 'grid_static.aria_pause_videos',
                    defaultMessage: 'Pause videos',
                  })
                : formatMessage({
                    id: 'grid_static.aria_play_videos',
                    defaultMessage: 'Play videos',
                  })
            }
            onClick={onTogglePlayPause}
          >
            {isPlayingAllVideos ? <StyledPauseIcon2 /> : <StyledPlayIcon2 />}
          </PlayPauseButton>
        )}
      </ControlsLeft>
      {hasOverflow && (
        <ControlsRight>
          <ScrollbarTrack aria-hidden="true">
            <ScrollbarThumb
              ref={scrollbarThumbRef}
              $widthPercent={thumbWidthPercent}
              $isReady={isScrollbarThumbReady}
            />
          </ScrollbarTrack>
          <ScrollNavControls>
            {canScrollLeft ? (
              <ScrollNavControlSlot>
                <ScrollNavButton
                  type="button"
                  aria-label={formatMessage({
                    id: 'grid_static.aria_scroll_discovery_guide_left',
                    defaultMessage: 'Scroll discovery guide left',
                  })}
                  onClick={onScrollLeft}
                >
                  <ScrollNavArrowIconLeft />
                </ScrollNavButton>
              </ScrollNavControlSlot>
            ) : (
              <ScrollNavDivider aria-hidden="true" />
            )}
            {canScrollRight ? (
              <ScrollNavControlSlot>
                <ScrollNavButton
                  type="button"
                  aria-label={formatMessage({
                    id: 'grid_static.aria_scroll_discovery_guide_right',
                    defaultMessage: 'Scroll discovery guide right',
                  })}
                  onClick={onScrollRight}
                >
                  <ScrollNavArrowIcon />
                </ScrollNavButton>
              </ScrollNavControlSlot>
            ) : (
              <ScrollNavDivider aria-hidden="true" />
            )}
          </ScrollNavControls>
        </ControlsRight>
      )}
    </DiscoveryGuideControlsRow>
  );
};

export default DiscoveryGuideControls;
